import { useState } from "react";
import { useMutation, useQuery } from "convex/react";
import { ChevronDown, History, ShieldAlert, Target } from "lucide-react";
import { api } from "../../convex/_generated/api";
import type { Id } from "../../convex/_generated/dataModel";
import { EmptyState } from "../components/ui/EmptyState";
import { Skeleton } from "../components/ui/Skeleton";
import { AdjustmentsListView } from "../components/progress/AdjustmentsList";
import type { EngineSnapshot, JourneySummary } from "../components/progress/utils";
import { AuditDisclosure, WhyTargetDisclosure } from "../components/plan/AuditDisclosure";
import { DailyTargetsCard } from "../components/plan/DailyTargetsCard";
import { EnergyBudgetCard } from "../components/plan/EnergyBudgetCard";
import { ForecastCard } from "../components/plan/ForecastCard";
import { GoalJourneyHero } from "../components/plan/GoalJourneyHero";
import { PlanDataQualityCard } from "../components/plan/PlanDataQualityCard";
import { PlanUpdateReadyCard } from "../components/plan/WeeklyCheckinCard";
import { ProgrammeSheet, type ProgrammeSheetKind } from "../components/plan/ProgrammeSheet";
import {
  createCheckinResponder,
  type CalculationLogEntry,
  type CheckinView,
  type PlanProfile,
  type ProgrammeUpdate,
} from "./plan-utils";
import { DEMO_PLAN_NOW, DEMO_SCENARIOS } from "../lib/demo-scenarios";
import { useDemoMode } from "../lib/demo-mode";

function SafetyBanner() {
  return (
    <div
      className="flex items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-4"
      role="status"
      data-testid="plan-safety-banner"
    >
      <ShieldAlert size={20} className="mt-0.5 shrink-0 text-amber-600" />
      <div className="flex flex-col gap-1">
        <p className="text-sm font-semibold text-amber-800">Your plan is in safety mode</p>
        <p className="text-xs leading-relaxed text-amber-700">
          Based on your answers, your plan avoids calorie restriction and follows maintenance-level guidance.
          Please work with a healthcare professional before pursuing weight change.
        </p>
      </div>
    </div>
  );
}

function PlanDetails({ history, engineVersion, checkins }: {
  readonly history: readonly CalculationLogEntry[];
  readonly engineVersion: string;
  readonly checkins: readonly CheckinView[];
}) {
  return (
    <section className="flex flex-col gap-2" data-testid="plan-details">
      <h3 className="px-1 text-xs font-semibold uppercase tracking-[0.14em] text-text-muted">Plan details</h3>
      <WhyTargetDisclosure history={history} />
      <details className="rounded-2xl border border-border bg-surface p-4 shadow-sm" data-testid="adjustments-disclosure">
        <summary className="flex min-h-[44px] cursor-pointer list-none items-center gap-2 text-base font-semibold text-text active:opacity-80">
          <History size={18} className="text-accent" />
          Adjustment history
          <ChevronDown size={16} className="disclosure-chevron ml-auto text-text-muted" aria-hidden />
        </summary>
        <div className="mt-2">
          <AdjustmentsListView rows={checkins} />
        </div>
      </details>
      <AuditDisclosure history={history} engineVersion={engineVersion} />
    </section>
  );
}

export interface PlanViewProps {
  readonly profile: PlanProfile;
  readonly snapshot: EngineSnapshot;
  readonly pending: CheckinView | null;
  readonly checkins: readonly CheckinView[];
  readonly calcHistory: readonly CalculationLogEntry[];
  readonly journey: JourneySummary;
  readonly onRespond: (checkinId: string, accept: boolean) => Promise<unknown>;
  readonly onSaveProgramme: (patch: ProgrammeUpdate) => Promise<unknown>;
}

/** Presentational Plan screen — shared by the live screen and the demo variant. */
export function PlanView({
  profile,
  snapshot,
  pending,
  checkins,
  calcHistory,
  journey,
  onRespond,
  onSaveProgramme,
}: PlanViewProps) {
  const [sheet, setSheet] = useState<ProgrammeSheetKind | null>(null);
  const [confirmation, setConfirmation] = useState<string | null>(null);

  const handleSaved = () => {
    setSheet(null);
    setConfirmation("Programme updated — your targets were recalculated.");
  };

  return (
    <div className="flex flex-col gap-4" data-testid="plan-container">
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-accent">Your programme</p>
      </div>
      {snapshot.restricted && <SafetyBanner />}
      {confirmation !== null && (
        <p
          role="status"
          className="rounded-xl bg-accent-light/60 px-3 py-2 text-xs font-medium leading-relaxed text-accent-dark"
          data-testid="programme-updated-banner"
        >
          {confirmation}
        </p>
      )}
      <GoalJourneyHero
        profile={profile}
        journey={journey}
        onEditGoal={() => setSheet("goal")}
      />
      {pending !== null && <PlanUpdateReadyCard pending={pending} onRespond={onRespond} />}
      <DailyTargetsCard
        profile={profile}
        snapshot={snapshot}
        checkins={checkins}
        onEditPreferences={() => setSheet("preferences")}
      />
      <ForecastCard
        snapshot={snapshot}
        pending={pending}
        checkins={checkins}
        planStartedAt={profile.planStartedAt}
      />
      <EnergyBudgetCard snapshot={snapshot} />
      <PlanDataQualityCard snapshot={snapshot} />
      <PlanDetails history={calcHistory} engineVersion={snapshot.engineVersion} checkins={checkins} />
      {sheet !== null && (
        <ProgrammeSheet
          kind={sheet}
          profile={profile}
          onSave={onSaveProgramme}
          onSaved={handleSaved}
          onClose={() => setSheet(null)}
        />
      )}
    </div>
  );
}

function toCheckinView(row: {
  _id: Id<"weeklyCheckins">;
  createdAt: number;
  weekStartDate?: number;
  previousTargetKcal: number;
  newTargetKcal: number;
  adjustmentKcal: number;
  adjusted: boolean;
  paused: boolean;
  explanation: string;
  reasonsJson: string;
  status?: string;
}): CheckinView {
  return {
    id: row._id,
    createdAt: row.createdAt,
    weekStartDate: row.weekStartDate,
    previousTargetKcal: row.previousTargetKcal,
    newTargetKcal: row.newTargetKcal,
    adjustmentKcal: row.adjustmentKcal,
    adjusted: row.adjusted,
    paused: row.paused,
    explanation: row.explanation,
    reasonsJson: row.reasonsJson,
    status: row.status,
  };
}

export default function Plan() {
  const profile = useQuery(api.profile.getProfile, {});
  const snapshot = useQuery(api.engine.getEngineState, {});
  const pending = useQuery(api.engine.getPendingCheckin, {});
  const checkins = useQuery(api.engine.getWeeklyCheckins, { limit: 6 });
  const calcHistory = useQuery(api.engine.getCalculationHistory, { limit: 50 });
  const journey = useQuery(api.engine.getJourneySummary, {});
  const respondMutation = useMutation(api.engine.respondToWeeklyCheckin);
  const updateProgramme = useMutation(api.engine.updateProgramme);

  const loading =
    profile === undefined ||
    snapshot === undefined ||
    pending === undefined ||
    checkins === undefined ||
    calcHistory === undefined ||
    journey === undefined;

  if (loading) {
    return (
      <div className="flex flex-col gap-4" data-testid="plan-container">
        <Skeleton className="h-10 w-40 rounded-xl" />
        <Skeleton className="h-32 rounded-2xl" />
        <Skeleton className="h-36 rounded-2xl" />
        <Skeleton className="h-36 rounded-2xl" />
        <Skeleton className="h-40 rounded-2xl" />
      </div>
    );
  }

  if (snapshot === null || profile === null || journey === null) {
    return (
      <div className="flex flex-col gap-4" data-testid="plan-container">
        <EmptyState
          icon={Target}
          title="Your plan is being calculated"
          message="Log a few days of meals and weigh-ins to unlock targets and projections."
        />
      </div>
    );
  }

  const typedCheckins = checkins as {
    _id: Id<"weeklyCheckins">;
    createdAt: number;
    weekStartDate?: number;
    previousTargetKcal: number;
    newTargetKcal: number;
    adjustmentKcal: number;
    adjusted: boolean;
    paused: boolean;
    explanation: string;
    reasonsJson: string;
    status?: string;
  }[];
  const typedHistory = calcHistory as CalculationLogEntry[];
  return (
    <PlanView
      profile={profile}
      snapshot={snapshot}
      pending={pending === null ? null : toCheckinView(pending)}
      checkins={typedCheckins.map(toCheckinView)}
      calcHistory={typedHistory}
      journey={journey}
      onRespond={createCheckinResponder(respondMutation)}
      onSaveProgramme={(patch) => updateProgramme(patch)}
    />
  );
}

// --- Demo variant ---------------------------------------------------------

/**
 * Scenario-driven demo variant — demo mode has no authenticated engine
 * queries, so this renders the active scenario's fixed dataset. Check-in
 * responses resolve locally: accepting a proposal applies the new target to
 * the local snapshot/profile so the recording shows the change immediately.
 */
export function DemoPlan() {
  const { scenario } = useDemoMode();
  const data = DEMO_SCENARIOS[scenario].plan;
  const [local, setLocal] = useState<{
    readonly scenario: typeof scenario;
    readonly pending: CheckinView | null;
    readonly acceptedTargetKcal: number | null;
    readonly resolved: CheckinView | null;
  }>({ scenario, pending: data.pending, acceptedTargetKcal: null, resolved: null });
  if (local.scenario !== scenario) {
    console.log("[Frontend][Demo] Plan re-seeded for scenario", { from: local.scenario, to: scenario });
    setLocal({ scenario, pending: data.pending, acceptedTargetKcal: null, resolved: null });
  }

  const profile: PlanProfile = local.acceptedTargetKcal === null
    ? data.profile
    : { ...data.profile, dailyGoalKcal: local.acceptedTargetKcal };
  const snapshot: EngineSnapshot = local.acceptedTargetKcal === null
    ? data.snapshot
    : { ...data.snapshot, targetKcal: local.acceptedTargetKcal };
  const checkins: readonly CheckinView[] = local.resolved === null
    ? data.checkins
    : [local.resolved, ...data.checkins.filter((row) => row.id !== local.resolved?.id)];
  const startKg = profile.startingWeightKg ?? profile.weightKg ?? data.currentWeightKg;
  const targetKg = profile.targetWeightKg ?? null;
  const direction = targetKg === null ? null : targetKg < startKg ? "lose" : targetKg > startKg ? "gain" : "maintain";
  const totalDistance = targetKg === null ? null : targetKg - startKg;
  const fraction = totalDistance === null || totalDistance === 0
    ? null
    : (data.currentWeightKg - startKg) / totalDistance;
  const journey: JourneySummary = {
    anchor: {
      startKg,
      startDateMs: profile.planStartedAt ?? DEMO_PLAN_NOW,
      anchorSource: "onboarding",
    },
    targetKg,
    direction,
    latestScale: { weightKg: data.currentWeightKg, dateMs: DEMO_PLAN_NOW },
    currentTrendKg: snapshot.trendKg,
    weighInDayCount: snapshot.trendConfidence === "high" ? 14 : snapshot.trendConfidence === "moderate" ? 7 : 2,
    trendMature: snapshot.trendConfidence === "high" || snapshot.trendConfidence === "moderate",
    progressBasis: snapshot.trendConfidence === "high" || snapshot.trendConfidence === "moderate" ? "trend" : "scale",
    progressFractionUnclamped: fraction,
    percentClamped: direction === "maintain" || fraction === null
      ? null
      : Math.round(Math.min(1, Math.max(0, fraction)) * 100),
    changeFromStartKg: Math.round((data.currentWeightKg - startKg) * 10) / 10,
    remainingKg: targetKg === null ? null : Math.round(Math.abs(targetKg - data.currentWeightKg) * 10) / 10,
    plannedRateKgPerWeek: snapshot.weeklyChangeKg,
    expenditureKind: snapshot.expenditureKind,
    forecastAvailable: snapshot.expenditureKind === "personalised",
    goalDateMin: snapshot.expenditureKind === "personalised" ? snapshot.goalDateMin : null,
    goalDateMax: snapshot.expenditureKind === "personalised" ? snapshot.goalDateMax : null,
  };

  const handleRespond = (_checkinId: string, accept: boolean) => {
    const pending = local.pending;
    if (pending !== null) {
      console.log("[Frontend][Demo] Plan update resolved locally", {
        scenario,
        accept,
        previousTargetKcal: pending.previousTargetKcal,
        newTargetKcal: pending.newTargetKcal,
      });
      setLocal((current) => ({
        ...current,
        pending: null,
        acceptedTargetKcal: accept ? pending.newTargetKcal : current.acceptedTargetKcal,
        resolved: { ...pending, status: accept ? "accepted" : "kept" },
      }));
    }
    return Promise.resolve(null);
  };

  return (
    <PlanView
      profile={profile}
      snapshot={snapshot}
      pending={local.pending}
      checkins={checkins}
      calcHistory={data.calcHistory}
      journey={journey}
      onRespond={handleRespond}
      onSaveProgramme={() => Promise.resolve(snapshot)}
    />
  );
}
